import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ImageOutlined } from '@mui/icons-material';
import { PRIMARY } from '../constants';
import { ContentBrowserModal } from '../ContentBrowserModal';

// ── Canvas helpers ────────────────────────────────────────────────────────────
export const CANVAS_HOTSPOT = {
  id: 'canvas',
  shape: 'rect',
  points: [{ x: 0, y: 0 }],
  meta: { width: 100, height: 100 },
};

export function CanvasHotspotHitRect({ selected, onMouseDown, onClick }) {
  return (
    <rect x={0} y={0} width="100%" height="100%"
      fill="transparent" stroke="none"
      style={{ cursor: selected ? 'move' : 'grab' }}
      onMouseDown={onMouseDown}
      onClick={onClick}
    />
  );
}

export function DrawPreview({ drawState, activeTool }) {
  if (!drawState || !drawState.active) return null;
  const { startX, startY, curX, curY } = drawState;

  if (activeTool === 'rect') {
    const x = Math.min(startX, curX);
    const y = Math.min(startY, curY);
    const w = Math.abs(curX - startX);
    const h = Math.abs(curY - startY);
    return <rect x={x} y={y} width={w} height={h} fill={alpha(PRIMARY, 0.15)} stroke={PRIMARY} strokeWidth={2} strokeDasharray="6 3" style={{ pointerEvents: 'none' }} />;
  }

  if (activeTool === 'circle') {
    const dx = curX - startX;
    const dy = curY - startY;
    const r  = Math.sqrt(dx * dx + dy * dy);
    return <circle cx={startX} cy={startY} r={r} fill={alpha('#00a882', 0.15)} stroke="#00a882" strokeWidth={2} strokeDasharray="6 3" style={{ pointerEvents: 'none' }} />;
  }

  return null;
}

// ── ElementContextMenu ────────────────────────────────────────────────────────
export function ElementContextMenu({ x, y, onDelete, onClose }) {
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    const handler = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed', top: y, left: x,
        background: '#fff', border: '1px solid #e4e9f0',
        borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.14)',
        padding: '4px 0', zIndex: 9999, minWidth: 140,
      }}
    >
      <div
        onMouseDown={e => { e.stopPropagation(); onDelete(); onClose(); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 14px', cursor: 'pointer', fontSize: 13,
          fontWeight: 500, color: '#dc2626',
          transition: 'background 0.1s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
        </svg>
        Delete
      </div>
    </div>,
    document.body
  );
}

// ── ThumbPickerInline ─────────────────────────────────────────────────────────
export function ThumbPickerInline({ value, onChange, stopAll }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Box onMouseDown={stopAll} onClick={e => { stopAll(e); setOpen(true); }}
        sx={{
          display: 'flex', alignItems: 'center', gap: '6px', px: 0.75, py: 0.5,
          borderRadius: '6px', border: '1px solid #e4e9f0', background: '#f8f9fb',
          cursor: 'pointer', '&:hover': { borderColor: '#c4cdd8', background: '#f0f4f8' },
        }}>
        {value
          ? <Box component="img" src={value} sx={{ width: 24, height: 24, borderRadius: '3px', objectFit: 'cover', flexShrink: 0, border: '1px solid #e4e9f0' }} />
          : <ImageOutlined sx={{ fontSize: 14, color: '#c4cdd8' }} />}
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: value ? PRIMARY : '#9ea8b3' }}>
          {value ? 'Change' : 'Pick Image'}
        </Typography>
        {value && (
          <Box onClick={e => { e.stopPropagation(); onChange(null); }}
            sx={{ fontSize: 10, color: '#c4cdd8', ml: 'auto', '&:hover': { color: '#dc2626' } }}>✕</Box>
        )}
      </Box>
      <ContentBrowserModal
        open={open}
        onClose={() => setOpen(false)}
        onSelect={url => { onChange(url); setOpen(false); }}
        mediaType="image"
      />
    </>
  );
}
